/* Owns the WS → TanStack Query mapping table of PANEL.md §11. Mounted once by
   `AppShell`; every socket message is applied synchronously to the cache here so
   no page has to know a socket exists. An unknown `type` is ignored, never
   thrown — the server union may grow ahead of the panel (API.md §21.3). */

import type {
  BanDto,
  CommandDto,
  DetectionDto,
  PlayerDto,
  ScreenshotDto,
  ServerDto,
  StatsHealth,
  WsServerMessage,
} from '@sentinelac/shared'
import { useQueryClient, type QueryClient, type QueryKey } from '@tanstack/react-query'
import { useEffect } from 'react'

import { backfillActiveBans, backfillDetections, healthQueryOptions } from '../app/api.js'
import { getAuthState } from '../lib/auth-store.js'
import { qk } from '../lib/queryKeys.js'
import { wsClient } from '../lib/ws.js'
import { pushToast } from '../components/ui/Toast.js'
import { translate, type Lang } from '../i18n/index.js'
import { getFeedState, mergeFeedItems, noteNewRows, pushFeedItem, type FeedItem } from './feedStore.js'

// ───────────────────────────── list cache helpers ─────────────────────────────
// Every list is `Paginated<T>` consumed with `useInfiniteQuery`, so a cached
// entry is `{ pages: Paginated<T>[] }`. The bare-page shape is tolerated too,
// because a detail page may hold one page without the infinite wrapper.

interface PageLike<T> {
  items: T[]
  nextCursor: string | null
}

interface InfiniteLike<T> {
  pages: Array<PageLike<T>>
  pageParams: unknown[]
}

function isPageLike(value: unknown): value is PageLike<unknown> {
  return typeof value === 'object' && value !== null && Array.isArray((value as { items?: unknown }).items)
}

function isInfiniteLike(value: unknown): value is InfiniteLike<unknown> {
  return (
    typeof value === 'object' &&
    value !== null &&
    Array.isArray((value as { pages?: unknown }).pages) &&
    (value as { pages: unknown[] }).pages.every(isPageLike)
  )
}

/**
 * Rewrites the items of every cached page. The `as T[]` is the one assertion in
 * this file: the shape is verified above, but the element type is only knowable
 * from the query key the caller chose.
 */
function mapPages<T>(data: unknown, fn: (items: T[], pageIndex: number) => T[]): unknown {
  if (isInfiniteLike(data)) {
    return { ...data, pages: data.pages.map((page, index) => ({ ...page, items: fn(page.items as T[], index) })) }
  }
  if (isPageLike(data)) {
    return { ...data, items: fn(data.items as T[], 0) }
  }
  return data
}

function updateLists<T>(client: QueryClient, prefix: QueryKey, fn: (items: T[], pageIndex: number) => T[]): void {
  for (const query of client.getQueryCache().findAll({ queryKey: prefix })) {
    client.setQueryData(query.queryKey, (old: unknown) => mapPages<T>(old, fn))
  }
}

/** The filter object a list key carries in its last position, or `{}` for a keyless list. */
function filterOf(key: QueryKey): Record<string, unknown> {
  const last = key[key.length - 1]
  return typeof last === 'object' && last !== null && !Array.isArray(last) ? (last as Record<string, unknown>) : {}
}

function asArray(value: unknown): unknown[] {
  if (value === undefined || value === null) return []
  return Array.isArray(value) ? value : [value]
}

function matchesValue(filter: Record<string, unknown>, field: string, actual: string): boolean {
  const wanted = asArray(filter[field])
  return wanted.length === 0 || wanted.includes(actual)
}

type ListVerdict = 'prepend' | 'skip' | 'pill'

/**
 * PANEL.md §6.3: a live row is only prepended into a list whose filters it
 * actually satisfies, and never into one with a date range — a bounded history
 * view must not gain rows outside its bounds, so the page raises the
 * "yeni kayıtlar var" pill instead.
 */
function detectionVerdict(filter: Record<string, unknown>, detection: DetectionDto): ListVerdict {
  if (!matchesValue(filter, 'detectionKey', detection.detectionKey)) return 'skip'
  if (!matchesValue(filter, 'severity', detection.severity)) return 'skip'
  if (!matchesValue(filter, 'action', detection.actionTaken)) return 'skip'
  if (!matchesValue(filter, 'side', detection.side)) return 'skip'
  if (typeof filter['playerId'] === 'string' && filter['playerId'] !== detection.playerId) return 'skip'
  if (typeof filter['serverConfirmed'] === 'boolean' && filter['serverConfirmed'] !== detection.serverConfirmed) {
    return 'skip'
  }
  if (typeof filter['falsePositive'] === 'boolean' && filter['falsePositive'] !== detection.falsePositive) return 'skip'
  if (filter['from'] !== undefined || filter['to'] !== undefined) return 'pill'
  return 'prepend'
}

// ───────────────────────── debounced summary invalidation ─────────────────────
// A busy server emits several detections a second; invalidating the aggregates
// on each one would refetch the dashboard continuously for no new information.

const SUMMARY_DEBOUNCE_MS = 30_000
const lastSummaryInvalidate = new Map<string, number>()
const pendingSummaryTimer = new Map<string, ReturnType<typeof setTimeout>>()

function invalidateAggregates(client: QueryClient, serverId: string): void {
  lastSummaryInvalidate.set(serverId, Date.now())
  void client.invalidateQueries({ queryKey: ['stats', serverId, 'overview'] })
  void client.invalidateQueries({ queryKey: ['detections', serverId, 'summary'] })
}

function scheduleAggregateInvalidation(client: QueryClient, serverId: string): void {
  const last = lastSummaryInvalidate.get(serverId) ?? 0
  const elapsed = Date.now() - last
  if (elapsed >= SUMMARY_DEBOUNCE_MS) {
    invalidateAggregates(client, serverId)
    return
  }
  if (pendingSummaryTimer.has(serverId)) return
  pendingSummaryTimer.set(
    serverId,
    setTimeout(() => {
      pendingSummaryTimer.delete(serverId)
      invalidateAggregates(client, serverId)
    }, SUMMARY_DEBOUNCE_MS - elapsed),
  )
}

// ─────────────────────────── in-tab command tracking ──────────────────────────

/** Commands this tab issued, so only their `acked`/`failed` transitions toast. */
const issuedCommands = new Set<string>()

export function trackIssuedCommand(commandId: string): void {
  issuedCommands.add(commandId)
}

// ─────────────────────────────── the mapping ──────────────────────────────────

function applyDetection(client: QueryClient, serverId: string, detection: DetectionDto): void {
  pushFeedItem({ kind: 'detection', id: detection.id, serverId, at: detection.receivedAt, detection })

  for (const query of client.getQueryCache().findAll({ queryKey: ['detections', serverId, 'list'] })) {
    const verdict = detectionVerdict(filterOf(query.queryKey), detection)
    if (verdict === 'skip') continue
    if (verdict === 'pill') {
      noteNewRows(serverId)
      continue
    }
    client.setQueryData(query.queryKey, (old: unknown) =>
      mapPages<DetectionDto>(old, (items, pageIndex) =>
        // Only page 0 grows: appending to a later page would break the cursor
        // ordering the next fetch relies on.
        pageIndex === 0 && !items.some((row) => row.id === detection.id) ? [detection, ...items] : items,
      ),
    )
  }

  if (detection.playerId !== null) {
    const playerId = detection.playerId
    updateLists<PlayerDto>(client, ['players', serverId, 'list'], (items) =>
      items.map((row) => (row.id === playerId ? { ...row, detectionCount: row.detectionCount + 1 } : row)),
    )
  }

  scheduleAggregateInvalidation(client, serverId)
}

function applyBan(client: QueryClient, serverId: string, ban: BanDto): void {
  pushFeedItem({ kind: 'ban', id: ban.id, serverId, at: ban.createdAt, ban })

  for (const query of client.getQueryCache().findAll({ queryKey: ['bans', serverId, 'list'] })) {
    const filter = filterOf(query.queryKey)
    const status = filter['status']
    // A fresh ban belongs in the active list and in the unfiltered one; it can
    // never belong in `expired` or `revoked`.
    if (status !== undefined && status !== 'active' && status !== 'all') continue
    if (filter['from'] !== undefined || filter['to'] !== undefined) continue
    client.setQueryData(query.queryKey, (old: unknown) =>
      mapPages<BanDto>(old, (items, pageIndex) =>
        pageIndex === 0 && !items.some((row) => row.id === ban.id) ? [ban, ...items] : items,
      ),
    )
  }

  if (ban.playerId !== null) {
    const playerId = ban.playerId
    updateLists<PlayerDto>(client, ['players', serverId, 'list'], (items) =>
      items.map((row) => (row.id === playerId ? { ...row, banCount: row.banCount + 1 } : row)),
    )
    void client.invalidateQueries({ queryKey: qk.player(serverId, playerId) })
  }

  scheduleAggregateInvalidation(client, serverId)
}

function applyUnban(client: QueryClient, serverId: string, banId: string, revokedByLabel: string): void {
  // `revokedBy` on the DTO is a user id; the message carries a display name, so
  // only `active` is patched here and the label rides the revoke reason field
  // the drawer already renders as text.
  updateLists<BanDto>(client, ['bans', serverId, 'list'], (items) =>
    items.map((row) => (row.id === banId ? { ...row, active: false, revokeReason: revokedByLabel } : row)),
  )
  void client.invalidateQueries({ queryKey: qk.ban(serverId, banId) })
}

function applyServerStatus(
  client: QueryClient,
  serverId: string,
  data: {
    status: StatsHealth['status']
    playersOnline: number
    lastHeartbeatAt: string | null
    agentVersion: string | null
    agentConfigVersion: number
  },
): void {
  client.setQueryData(qk.health(serverId), (old: StatsHealth | undefined) =>
    old
      ? {
          ...old,
          status: data.status,
          playersOnline: data.playersOnline,
          lastHeartbeatAt: data.lastHeartbeatAt,
          agentVersion: data.agentVersion,
          agentConfigVersion: data.agentConfigVersion,
          configSynced: data.agentConfigVersion === old.configVersion,
        }
      : old,
  )

  client.setQueryData(qk.server(serverId), (old: ServerDto | undefined) =>
    old
      ? {
          ...old,
          status: data.status,
          playersOnline: data.playersOnline,
          lastHeartbeatAt: data.lastHeartbeatAt,
          agentVersion: data.agentVersion,
          agentConfigVersion: data.agentConfigVersion,
        }
      : old,
  )

  updateLists<ServerDto>(client, qk.servers(), (items) =>
    items.map((row) =>
      row.id === serverId
        ? {
            ...row,
            status: data.status,
            playersOnline: data.playersOnline,
            lastHeartbeatAt: data.lastHeartbeatAt,
            agentVersion: data.agentVersion,
            agentConfigVersion: data.agentConfigVersion,
          }
        : row,
    ),
  )
}

function applyPlayerJoin(client: QueryClient, serverId: string, player: PlayerDto): void {
  for (const query of client.getQueryCache().findAll({ queryKey: ['players', serverId, 'list'] })) {
    const onlineOnly = filterOf(query.queryKey)['online'] === true
    client.setQueryData(query.queryKey, (old: unknown) =>
      mapPages<PlayerDto>(old, (items, pageIndex) => {
        const known = items.some((row) => row.id === player.id)
        if (known) return items.map((row) => (row.id === player.id ? { ...row, ...player, online: true } : row))
        return onlineOnly && pageIndex === 0 ? [player, ...items] : items
      }),
    )
  }
  client.setQueryData(qk.player(serverId, player.id), (old: unknown) =>
    old && typeof old === 'object' && 'player' in old ? { ...old, player } : old,
  )
}

function applyPlayerLeave(client: QueryClient, serverId: string, playerId: string): void {
  for (const query of client.getQueryCache().findAll({ queryKey: ['players', serverId, 'list'] })) {
    const onlineOnly = filterOf(query.queryKey)['online'] === true
    client.setQueryData(query.queryKey, (old: unknown) =>
      mapPages<PlayerDto>(old, (items) =>
        onlineOnly
          ? items.filter((row) => row.id !== playerId)
          : items.map((row) => (row.id === playerId ? { ...row, online: false } : row)),
      ),
    )
  }
  void client.invalidateQueries({ queryKey: qk.player(serverId, playerId) })
}

function applyCommandUpdated(client: QueryClient, serverId: string, command: CommandDto, lang: Lang): void {
  updateLists<CommandDto>(client, ['commands', serverId, 'list'], (items) =>
    items.map((row) => (row.id === command.id ? command : row)),
  )
  if (!issuedCommands.has(command.id)) return
  if (command.status === 'acked') {
    issuedCommands.delete(command.id)
    pushToast({ variant: 'success', title: translate(lang, `command.status.${command.status}`) })
  } else if (command.status === 'failed' || command.status === 'expired') {
    issuedCommands.delete(command.id)
    pushToast({
      variant: 'danger',
      title: translate(lang, `command.status.${command.status}`),
      ...(command.error ? { description: command.error } : {}),
    })
  }
}

function applyScreenshotUpdated(client: QueryClient, serverId: string, screenshot: ScreenshotDto): void {
  updateLists<ScreenshotDto>(client, ['screenshots', serverId, 'list'], (items) =>
    items.map((row) => (row.id === screenshot.id ? screenshot : row)),
  )
  if (screenshot.detectionId !== null) {
    client.setQueryData(qk.detection(serverId, screenshot.detectionId), (old: unknown) =>
      old && typeof old === 'object' && 'detection' in old ? { ...old, screenshot } : old,
    )
  }
}

/**
 * The one place a socket message becomes cache state. `client` and `lang` are
 * passed in rather than read from a hook so this is testable without a renderer.
 */
export function applyWsMessage(client: QueryClient, message: WsServerMessage, lang: Lang): void {
  switch (message.type) {
    case 'hello':
      // The hub replays nothing, so a resumed socket must re-read what it missed.
      void runBackfill(client)
      return
    case 'detection':
      applyDetection(client, message.serverId, message.data)
      return
    case 'ban':
      applyBan(client, message.serverId, message.data)
      return
    case 'unban':
      applyUnban(client, message.serverId, message.data.banId, message.data.revokedBy)
      return
    case 'server.status':
      applyServerStatus(client, message.serverId, message.data)
      return
    case 'player.join':
      applyPlayerJoin(client, message.serverId, message.data)
      return
    case 'player.leave':
      applyPlayerLeave(client, message.serverId, message.data.playerId)
      return
    case 'command.updated':
      applyCommandUpdated(client, message.serverId, message.data, lang)
      return
    case 'screenshot.updated':
      applyScreenshotUpdated(client, message.serverId, message.data)
      return
    case 'config.updated':
      void client.invalidateQueries({ queryKey: qk.config(message.serverId) })
      void client.invalidateQueries({ queryKey: qk.configVersions(message.serverId) })
      void client.invalidateQueries({ queryKey: qk.health(message.serverId) })
      return
    case 'error':
      pushToast({ variant: 'danger', title: translate(lang, `errors.${message.data.code}`) })
      return
    case 'positions':
      // Deliberately not cached. A roster snapshot lands every few seconds and
      // is worthless once the next one does; routing it through the query cache
      // would re-render the panel on a timer for data only the map reads. The
      // map subscribes to `wsClient.onMessage` directly instead.
      return
    case 'subscribed':
    case 'pong':
      // Handled inside `lib/ws.ts`; nothing in the cache depends on them.
      return
    default:
      return
  }
}

/** API.md §21.5 — after every `hello`, re-read what the fire-and-forget hub could not replay. */
async function runBackfill(client: QueryClient): Promise<void> {
  const serverId = getAuthState().activeServerId
  if (!serverId) return

  const newest = newestHeldReceivedAt(serverId)
  const [detections, bans] = await Promise.allSettled([
    backfillDetections(serverId, newest),
    backfillActiveBans(serverId),
  ])

  if (detections.status === 'fulfilled') {
    mergeFeedItems(
      detections.value.items.map<FeedItem>((detection) => ({
        kind: 'detection',
        id: detection.id,
        serverId,
        at: detection.receivedAt,
        detection,
      })),
    )
  }
  if (bans.status === 'fulfilled') {
    mergeFeedItems(
      bans.value.items.map<FeedItem>((ban) => ({ kind: 'ban', id: ban.id, serverId, at: ban.createdAt, ban })),
    )
  }
  await client.refetchQueries({ queryKey: healthQueryOptions(serverId).queryKey })
}

/** The ring buffer is newest-first, so the head is the `from=` bound the backfill needs. */
function newestHeldReceivedAt(serverId: string): string | undefined {
  return getFeedState().items.find((item) => item.serverId === serverId)?.at
}

/**
 * Mounted once by `AppShell`. Every message lands in the cache before any page
 * renders, so a component can never observe a half-applied event.
 */
export function useServerFeed(lang: Lang): void {
  const client = useQueryClient()
  useEffect(() => {
    return wsClient.onMessage((message) => applyWsMessage(client, message, lang))
  }, [client, lang])
}
